// frontend-dash/src/pages/EditableBlogPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import AuthContext from '../context/AuthContext';
import { trackAdminBlogUpdated } from '../utils/analytics';
import EditableField from '../components/EditableField';
import MD3BlogCarousel from '../components/MD3BlogCarousel';
import {
    ArrowLeftIcon as ArrowLeft, CalendarIcon as Calendar,
    UserIcon as User, EditIcon as Edit2,
    CheckIcon as Check, DeleteIcon as Trash,
    UploadIcon, RefreshIcon as RefreshCw
} from '../components/Icons';

const getMainUrl = () => {
    if (process.env.REACT_APP_MAIN_URL) return process.env.REACT_APP_MAIN_URL;
    if (typeof window !== 'undefined' && window.location.hostname) {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return 'http://localhost:3000';
        }
        return `${window.location.protocol}//${window.location.hostname.replace(/^dash\./, '')}`;
    }
    return '';
};

const MAIN_URL = getMainUrl();

// Lazy load ReactQuill
let ReactQuill = null;
try {
    ReactQuill = require('react-quill-new').default;
    require('react-quill-new/dist/quill.snow.css');
} catch (e) {
    console.warn('react-quill-new not available, falling back to textarea');
}

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link', 'image'],
        ['clean'],
    ],
};

const EditableBlogPage = () => {
    const { id } = useParams();
    const { user, API_URL, PUBLIC_API_URL, getAssetUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const resolveAsset = (url) => {
        if (getAssetUrl) return getAssetUrl(url);
        return url;
    };

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveMsg, setSaveMsg] = useState('');
    const [contentEditing, setContentEditing] = useState(false);
    const [editedContent, setEditedContent] = useState('');
    const [events, setEvents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [urlInput, setUrlInput] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [postRes, eventsRes, groupsRes] = await Promise.all([
                    axios.get(`${API_URL}/blog/${id}`),
                    axios.get(`${API_URL}/events`),
                    axios.get(`${API_URL}/groups`)
                ]);
                setPost(postRes.data);
                setEditedContent(postRes.data.content || '');
                setEvents(eventsRes.data);
                setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
            } catch (err) {
                setError('Blogbeitrag konnte nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, API_URL]);

    const saveField = async (field, value) => {
        try {
            const config = {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
            };
            const updateData = { ...post, [field]: value };
            await axios.put(`${API_URL}/blog/${id}`, updateData, config);
            setPost(prev => ({ ...prev, [field]: value }));
            trackAdminBlogUpdated(post.title);
            setSaveMsg('Gespeichert!');
            setTimeout(() => setSaveMsg(''), 2000);
        } catch (err) {
            console.error('Save failed:', err);
            throw err;
        }
    };

    const saveImages = async (newImagesList) => {
        try {
            const config = {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
            };
            const firstImg = newImagesList.length > 0 ? newImagesList[0] : null;
            const primaryImgUrl = typeof firstImg === 'string' ? firstImg : (firstImg?.url || null);
            const updateData = {
                ...post,
                images: newImagesList.length > 0 ? newImagesList : null,
                imageUrl: primaryImgUrl
            };
            await axios.put(`${API_URL}/blog/${id}`, updateData, config);
            setPost(prev => ({
                ...prev,
                images: newImagesList,
                imageUrl: primaryImgUrl
            }));
            trackAdminBlogUpdated(post.title);
            setSaveMsg('Bilder gespeichert!');
            setTimeout(() => setSaveMsg(''), 2000);
        } catch (err) {
            console.error('Save images failed:', err);
            setError('Fehler beim Speichern der Bilder.');
        }
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };

            const uploadUrl = PUBLIC_API_URL ? `${PUBLIC_API_URL}/files/upload` : `${API_URL}/files/upload`;
            const uploadPromises = files.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                const res = await axios.post(uploadUrl, formData, config);
                return {
                    url: res.data.url,
                    title: file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " "),
                    caption: ''
                };
            });

            const uploadedItems = await Promise.all(uploadPromises);
            const currentImages = Array.isArray(post.images) && post.images.length > 0
                ? post.images.map(img => typeof img === 'string' ? { url: img, title: '', caption: '' } : img)
                : (post.imageUrl ? [{ url: post.imageUrl, title: '', caption: '' }] : []);
            const updated = [...currentImages, ...uploadedItems];
            await saveImages(updated);
        } catch (err) {
            console.error('Upload failed:', err);
            setError('Fehler beim Hochladen.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleAddImageUrl = async () => {
        if (!urlInput.trim()) return;
        const currentImages = Array.isArray(post.images) && post.images.length > 0
            ? post.images.map(img => typeof img === 'string' ? { url: img, title: '', caption: '' } : img)
            : (post.imageUrl ? [{ url: post.imageUrl, title: '', caption: '' }] : []);
        const updated = [...currentImages, { url: urlInput.trim(), title: '', caption: '' }];
        await saveImages(updated);
        setUrlInput('');
    };

    const handleRemoveImage = async (idxToRemove) => {
        const currentImages = Array.isArray(post.images) && post.images.length > 0
            ? post.images
            : (post.imageUrl ? [post.imageUrl] : []);
        const updated = currentImages.filter((_, idx) => idx !== idxToRemove);
        await saveImages(updated);
    };

    const handleSetPrimary = async (idx) => {
        const currentImages = Array.isArray(post.images) && post.images.length > 0
            ? post.images
            : (post.imageUrl ? [post.imageUrl] : []);
        const selected = currentImages[idx];
        const rest = currentImages.filter((_, i) => i !== idx);
        const updated = [selected, ...rest];
        await saveImages(updated);
    };

    const handleUpdateImageMeta = async (idx, field, value) => {
        const currentImages = (Array.isArray(post.images) && post.images.length > 0
            ? post.images
            : (post.imageUrl ? [{ url: post.imageUrl, title: '', caption: '' }] : [])
        ).map(img => typeof img === 'string' ? { url: img, title: '', caption: '' } : { ...img });

        currentImages[idx] = { ...currentImages[idx], [field]: value };
        await saveImages(currentImages);
    };

    const handleContentSave = async () => {
        await saveField('content', editedContent);
        setContentEditing(false);
    };

    const handleDelete = async () => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` },
            };
            await axios.delete(`${API_URL}/blog/${id}`, config);
            navigate('/blog');
        } catch (err) {
            console.error('Delete failed:', err);
            setError('Löschen fehlgeschlagen.');
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-surface flex items-center justify-center">
            <div className="md3-progress-circular border-4 border-primary border-t-transparent" />
            <span className="ml-4 text-surface-onVariant">Lädt Blogbeitrag...</span>
        </div>
    );

    if (error || !post) return (
        <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6">
            <div className="bg-error-container text-error-onContainer p-6 rounded-3xl max-w-md text-center">
                <h2 className="text-headline-small mb-2">Fehler</h2>
                <p>{error || 'Nicht gefunden.'}</p>
                <button onClick={() => navigate('/')} className="md3-btn-filled mt-4">Zurück</button>
            </div>
        </div>
    );

    const imagesList = Array.isArray(post.images) && post.images.length > 0
        ? post.images
        : (post.imageUrl ? [post.imageUrl] : []);

    return (
        <div className="min-h-screen bg-surface text-surface-on pb-20">
            {/* Edit Mode Banner */}
            <div className="edit-mode-banner px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md bg-surface/85 border-b border-outlineVariant/40">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/blog')}
                        className="w-8 h-8 rounded-full hover:bg-surface-on/5 flex items-center justify-center transition-colors"
                        aria-label="Zurück zur Blog-Übersicht"
                    >
                        <ArrowLeft sx={{ fontSize: 20 }} />
                    </button>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center edit-pen-glow">
                        <Edit2 sx={{ fontSize: 18 }} className="text-primary" />
                    </div>
                    <div>
                        <span className="text-label-large font-bold text-primary">Blog bearbeiten</span>
                        <span className="text-label-small text-surface-onVariant ml-2 hidden sm:inline">Klicke auf Felder um sie zu bearbeiten</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {saveMsg && <span className="text-label-medium text-primary font-bold animate-fade-in">✓ {saveMsg}</span>}
                    <button
                        type="button"
                        onClick={() => {
                            const rawImgs = Array.isArray(post.images) && post.images.length > 0
                                ? post.images
                                : (post.imageUrl ? [{ url: post.imageUrl }] : []);
                            const normalized = rawImgs.map(img => typeof img === 'string' ? { url: img, title: '', caption: '' } : img);
                            navigate('/instagram-generator', {
                                state: {
                                    title: post.title,
                                    subtitle: '',
                                    images: normalized
                                }
                            });
                        }}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
                        title="Diesen Blogbeitrag im Instagram Generator öffnen"
                    >
                        <span>📸</span>
                        <span className="hidden sm:inline">Instagram-Beitrag generieren</span>
                    </button>
                    <a href={`${MAIN_URL}/blog/${id}`} className="md3-btn-outlined text-label-medium">Zur Ansicht</a>
                </div>
            </div>

            <div className="container mx-auto px-4 pt-8 max-w-7xl relative z-10">
                <div className="flex flex-col lg:flex-row gap-8">
                    <article className="flex-1 md3-card-elevated bg-surface-containerLow p-6 md:p-12 shadow-elevation-2 rounded-[2.5rem] border border-outlineVariant/30">
                        {/* Metadata */}
                        <div className="flex flex-wrap items-center gap-4 md:gap-8 mb-8 text-surface-onVariant border-b border-outlineVariant pb-6">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                    <User size={20} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-label-small">Autor</span>
                                    <span className="text-title-small">
                                        {post.author?.userData?.scoutName || post.author?.userData?.firstName || 'Pfadfinder'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                                    <Calendar size={20} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-label-small">Veröffentlicht</span>
                                    <span className="text-title-small">{new Date(post.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                                </div>
                            </div>

                            {/* Status */}
                            <EditableField
                                value={post.status || 'draft'}
                                onSave={v => saveField('status', v)}
                                type="select"
                                label="Status"
                                selectOptions={[
                                    { value: 'draft', label: 'Entwurf' },
                                    { value: 'pending', label: 'Wartet auf Freigabe' },
                                    { value: 'published', label: 'Veröffentlicht' },
                                ]}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-full text-label-medium font-bold ${
                                        post.status === 'published' ? 'bg-primary/10 text-primary' :
                                        post.status === 'pending' ? 'bg-tertiary/10 text-tertiary' :
                                        'bg-surface-containerHigh text-surface-onVariant'
                                    }`}>
                                        {post.status === 'published' ? 'Veröffentlicht' : post.status === 'pending' ? 'Wartet' : 'Entwurf'}
                                    </span>
                                </div>
                            </EditableField>
                        </div>

                        {/* Title */}
                        <EditableField value={post.title} onSave={v => saveField('title', v)} label="Titel">
                            <h1 className="text-display-small md:text-display-medium font-bold text-surface-on mb-6 leading-tight">
                                {post.title}
                            </h1>
                        </EditableField>

                        {/* Prominent MD3 Carousel Preview */}
                        {imagesList.length > 0 && (
                            <MD3BlogCarousel
                                images={imagesList}
                                title={post.title}
                                getAssetUrl={resolveAsset}
                                className="mb-8"
                            />
                        )}

                        {/* Content */}
                        <div className="mb-12">
                            {!contentEditing ? (
                                <div className="editable-field group relative cursor-pointer" onClick={() => setContentEditing(true)} title="Inhalt bearbeiten">
                                    <div className="prose prose-lg dark:prose-invert max-w-none text-surface-onVariant leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
                                    <div className="editable-field-overlay"><Edit2 sx={{ fontSize: 16 }} /></div>
                                </div>
                            ) : (
                                <div className="editable-field-active rounded-2xl p-4 bg-primary/5 border-2 border-primary/30">
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <span className="text-label-small font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                                            <Edit2 sx={{ fontSize: 14 }} /> Inhalt
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => { setEditedContent(post.content); setContentEditing(false); }}
                                                className="p-1.5 rounded-lg hover:bg-surface-containerHigh text-surface-onVariant">Abbrechen</button>
                                            <button onClick={handleContentSave}
                                                className="px-3 py-1.5 rounded-lg bg-primary text-primary-on font-bold text-label-small flex items-center gap-1.5">
                                                <Check sx={{ fontSize: 16 }} /> Speichern
                                            </button>
                                        </div>
                                    </div>
                                    {ReactQuill ? (
                                        <ReactQuill theme="snow" value={editedContent} onChange={setEditedContent} modules={quillModules} />
                                    ) : (
                                        <textarea value={editedContent} onChange={e => setEditedContent(e.target.value)}
                                            className="w-full min-h-[400px] p-4 bg-surface-containerLowest border border-outlineVariant rounded-xl text-surface-on" />
                                    )}
                                </div>
                            )}
                        </div>
                    </article>

                    {/* Sidebar: Media Gallery & Settings */}
                    <aside className="lg:w-96 flex-shrink-0 space-y-6">
                        {/* Media Management Card */}
                        <div className="md3-card-outlined p-6 rounded-3xl bg-surface-containerLow border border-outlineVariant/40">
                            <h3 className="text-title-medium font-bold mb-4 flex items-center justify-between">
                                <span>Bildergalerie {imagesList.length > 0 && `(${imagesList.length})`}</span>
                                {isUploading && <RefreshCw className="animate-spin text-primary" sx={{ fontSize: 18 }} />}
                            </h3>

                            {/* Upload Zone */}
                            <label className="flex flex-col items-center justify-center w-full py-6 border-2 border-dashed border-outline/50 hover:border-primary rounded-2xl cursor-pointer hover:bg-surface-containerHigh transition-colors group mb-4">
                                <UploadIcon className="text-surface-onVariant group-hover:text-primary mb-2 transition-colors" sx={{ fontSize: 28 }} />
                                <span className="text-label-medium font-bold text-surface-on">
                                    {isUploading ? 'Lädt hoch...' : 'Bilder hochladen'}
                                </span>
                                <span className="text-label-small text-surface-onVariant">
                                    Mehrere Bilder möglich
                                </span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    multiple
                                    onChange={handleFileUpload}
                                    disabled={isUploading}
                                />
                            </label>

                            {/* Images Grid */}
                            {imagesList.length > 0 && (
                                <div className="space-y-3 mb-4">
                                    {imagesList.map((img, idx) => {
                                        const imgUrl = typeof img === 'string' ? img : (img?.url || '');
                                        const imgTitle = typeof img === 'string' ? '' : (img?.title || '');
                                        const imgCaption = typeof img === 'string' ? '' : (img?.caption || '');

                                        return (
                                            <div
                                                key={idx}
                                                className={`p-2.5 rounded-2xl bg-surface-containerHighest/60 border ${
                                                    idx === 0 ? 'border-primary' : 'border-outlineVariant/30'
                                                }`}
                                            >
                                                <div className="flex gap-2.5 items-start">
                                                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-black shrink-0 relative">
                                                        <img src={resolveAsset(imgUrl)} alt="" className="w-full h-full object-cover" />
                                                        {idx === 0 && (
                                                            <div className="absolute bottom-0.5 left-0.5 px-1 rounded bg-primary text-primary-on text-[9px] font-bold">
                                                                Titel
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0 space-y-1">
                                                        <input
                                                            type="text"
                                                            value={imgTitle}
                                                            onChange={e => handleUpdateImageMeta(idx, 'title', e.target.value)}
                                                            placeholder="Bildtitel (z. B. Baden im Freibad)"
                                                            className="w-full text-[11px] font-bold bg-surface-container border border-outlineVariant/30 rounded-lg px-2 py-1 text-surface-on focus:outline-none focus:border-primary"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={imgCaption}
                                                            onChange={e => handleUpdateImageMeta(idx, 'caption', e.target.value)}
                                                            placeholder="Kurze Beschreibung..."
                                                            className="w-full text-[10px] bg-surface-container border border-outlineVariant/30 rounded-lg px-2 py-0.5 text-surface-onVariant focus:outline-none focus:border-primary"
                                                        />
                                                        <div className="flex items-center justify-between pt-0.5">
                                                            {idx !== 0 ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSetPrimary(idx)}
                                                                    className="text-[10px] font-bold text-primary hover:underline cursor-pointer"
                                                                >
                                                                    ★ Als Titelbild
                                                                </button>
                                                            ) : <span className="text-[10px] text-primary font-bold">★ Hauptbild</span>}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveImage(idx)}
                                                                className="text-[10px] font-semibold text-error hover:underline cursor-pointer"
                                                            >
                                                                Löschen
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add by URL */}
                            <div className="flex gap-2">
                                <input
                                    type="url"
                                    value={urlInput}
                                    onChange={e => setUrlInput(e.target.value)}
                                    placeholder="Bild-URL einfügen..."
                                    className="md3-textfield-outlined text-label-medium flex-1 py-1.5"
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddImageUrl();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddImageUrl}
                                    className="md3-btn-tonal px-3 text-label-medium font-bold shrink-0"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Settings Card */}
                        <div className="md3-card-outlined p-6 rounded-3xl bg-surface-containerLow border border-outlineVariant/40">
                            <h3 className="text-title-medium font-bold mb-4 flex items-center gap-2">
                                <Edit2 sx={{ fontSize: 18 }} className="text-primary" />
                                Einstellungen
                            </h3>

                            {/* Linked Event */}
                            <EditableField
                                value={post.linkedEvent?._id || post.linkedEvent || ''}
                                onSave={v => saveField('linkedEvent', v || null)}
                                type="select"
                                label="Verknüpftes Event"
                                selectOptions={[
                                    { value: '', label: 'Kein Event verknüpft' },
                                    ...events.map(e => ({ value: e._id, label: `${e.title} (${new Date(e.date).toLocaleDateString()})` }))
                                ]}
                            >
                                <div className="p-3 rounded-lg bg-surface-container mb-4">
                                    <p className="text-label-small text-surface-onVariant">Verknüpftes Event</p>
                                    <p className="text-body-medium font-bold">
                                        {(() => {
                                            const eventId = post.linkedEvent?._id || post.linkedEvent;
                                            if (!eventId) return 'Keins';
                                            const ev = events.find(e => e._id === eventId);
                                            return ev ? ev.title : 'Verknüpft';
                                        })()}
                                    </p>
                                </div>
                            </EditableField>

                            {/* Linked Group / Stufe */}
                            <EditableField
                                value={post.linkedGroup?._id || post.linkedGroup?.slug || post.linkedGroup || ''}
                                onSave={v => saveField('linkedGroup', v || null)}
                                type="select"
                                label="Verknüpfte Gruppe / Stufe"
                                selectOptions={[
                                    { value: '', label: 'Keine Gruppe verknüpft' },
                                    ...groups.map(g => ({ value: g.slug || g._id, label: `${g.name} (${g.altersstufe || 'Gruppe'})` }))
                                ]}
                            >
                                <div className="p-3 rounded-lg bg-surface-container mb-4">
                                    <p className="text-label-small text-surface-onVariant">Verknüpfte Gruppe / Stufe</p>
                                    <p className="text-body-medium font-bold">
                                        {(() => {
                                            const groupVal = post.linkedGroup?.slug || post.linkedGroup?._id || post.linkedGroup;
                                            if (!groupVal) return 'Keine';
                                            const grp = groups.find(g => g._id === groupVal || g.slug === groupVal);
                                            return grp ? `${grp.name} (${grp.altersstufe || 'Gruppe'})` : (post.linkedGroup?.name || 'Verknüpft');
                                        })()}
                                    </p>
                                    <p className="text-[11px] text-surface-onVariant mt-0.5">Erscheint auf der Gruppen-Detailseite</p>
                                </div>
                            </EditableField>

                            {/* Background Color */}
                            {imagesList.length === 0 && (
                                <EditableField
                                    value={post.backgroundColor || '#cccccc'}
                                    onSave={v => saveField('backgroundColor', v)}
                                    type="color"
                                    label="Hintergrundfarbe"
                                >
                                    <div className="p-3 rounded-lg bg-surface-container mb-4">
                                        <p className="text-label-small text-surface-onVariant">Hintergrundfarbe (Fallback)</p>
                                        <div className="flex items-center gap-3 mt-1">
                                            <div className="w-8 h-8 rounded-lg border border-outlineVariant" style={{ backgroundColor: post.backgroundColor || '#cccccc' }}></div>
                                            <span className="font-mono text-label-medium font-bold text-primary">{(post.backgroundColor || '#cccccc').toUpperCase()}</span>
                                        </div>
                                    </div>
                                </EditableField>
                            )}

                            {/* Archived */}
                            <EditableField
                                value={post.isArchived}
                                onSave={v => saveField('isArchived', v)}
                                type="toggle"
                                label="Archiviert"
                            >
                                <div>
                                    <p className="font-bold text-surface-on">Archiviert</p>
                                    <p className="text-body-small text-surface-onVariant">Archivierte Beiträge werden nicht mehr angezeigt</p>
                                </div>
                            </EditableField>

                            <div className="mt-8 pt-6 border-t border-outlineVariant">
                                {deleteConfirm ? (
                                    <div className="bg-error-container/20 p-4 rounded-2xl border border-error/20">
                                        <p className="text-label-medium text-error font-bold mb-3 text-center">Endgültig löschen?</p>
                                        <div className="flex gap-2">
                                            <button onClick={handleDelete} className="flex-1 md3-btn-filled !bg-error !text-error-on text-label-small uppercase">Löschen</button>
                                            <button onClick={() => setDeleteConfirm(false)} className="flex-1 md3-btn-text text-label-small">Abbruch</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setDeleteConfirm(true)}
                                        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl border border-error/30 text-error hover:bg-error/5 transition-colors font-bold text-label-large"
                                    >
                                        <Trash sx={{ fontSize: 18 }} />
                                        Beitrag löschen
                                    </button>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default EditableBlogPage;
